import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Avatar from '@/components/Avatar';
import { I } from '@/lib/icons';
import { useWorkspace } from '@/contexts/WorkspaceContext';
import { useAuth } from '@/contexts/AuthContext';

function typeLabel(type) {
  return type === 'sro' ? 's.r.o.' : 'SZČO';
}

export default function AccountPanel() {
  const navigate = useNavigate();
  const { list, current, switchWorkspace } = useWorkspace();
  const { user, signOut } = useAuth();
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  const close = useCallback(() => setOpen(false), []);

  useEffect(() => {
    if (!open) return;
    const onDocClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) close();
    };
    const onKey = (e) => { if (e.key === 'Escape') { e.preventDefault(); close(); } };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open, close]);

  const handleSelect = (ws) => {
    if (ws.id !== current?.id) switchWorkspace(ws.id);
    close();
  };

  const goSettings = () => { close(); navigate('/nastavenia'); };

  const hasMultiple = (list?.length || 0) > 1;

  return (
    <div ref={wrapRef} style={{ position: 'relative' }}>
      <button
        type="button"
        data-testid="account-panel-trigger"
        onClick={() => setOpen(o => !o)}
        aria-haspopup="menu"
        aria-expanded={open}
        style={{
          width: '100%', padding: '8px 10px',
          display: 'flex', alignItems: 'center', gap: 10,
          background: open ? 'var(--bg-hover)' : 'rgba(0,0,0,0.04)',
          border: '1px solid var(--border)',
          borderRadius: 'var(--r-md)', cursor: 'pointer', textAlign: 'left',
          fontFamily: 'inherit',
        }}
      >
        <Avatar name={current?.name || 'Firma'} size={28} shape="square" />
        <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0, flex: 1 }}>
          <div style={{
            fontSize: 12.5, fontWeight: 600,
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          }}>
            {current?.name || 'Moja firma'}
          </div>
          <div style={{
            fontSize: 10.5, color: 'var(--text-muted)',
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          }}>
            {user?.email}
          </div>
        </div>
        <span style={{ color: 'var(--text-muted)', display: 'flex' }}>
          <I.chevD />
        </span>
      </button>

      {open && (
        <div
          role="menu"
          style={{
            position: 'absolute', bottom: 'calc(100% + 6px)', left: 0, right: 0,
            background: 'var(--bg-surface)',
            border: '1px solid var(--border)',
            borderRadius: 'var(--r-md)',
            boxShadow: '0 -12px 32px rgba(0,0,0,0.18)',
            zIndex: 60,
            overflow: 'hidden',
            display: 'flex', flexDirection: 'column',
            maxHeight: 'min(72vh, 420px)',
          }}
        >
          {/* Workspace list — only if >1 */}
          {hasMultiple && (
            <>
              <div style={{
                padding: '10px 14px 4px', fontSize: 10.5, fontWeight: 600,
                color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.04em',
              }}>Prepnúť firmu</div>
              <div style={{ overflowY: 'auto' }}>
                {list.map(ws => {
                  const isCurrent = ws.id === current?.id;
                  return (
                    <button
                      key={ws.id}
                      type="button"
                      data-testid={`account-panel-firma-${ws.id}`}
                      onClick={() => handleSelect(ws)}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 10,
                        padding: '8px 14px', width: '100%', textAlign: 'left',
                        background: 'transparent', border: 'none', cursor: 'pointer',
                        fontFamily: 'inherit',
                      }}
                      onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-hover)'}
                      onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                    >
                      <Avatar name={ws.name} size={22} shape="square" />
                      <div style={{ minWidth: 0, flex: 1 }}>
                        <div style={{
                          fontSize: 12.5, fontWeight: 500,
                          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                        }}>{ws.name}</div>
                        <div style={{ fontSize: 10.5, color: 'var(--text-muted)' }}>{typeLabel(ws.type)}</div>
                      </div>
                      {isCurrent && (
                        <span style={{ color: 'var(--product-primary-hover)', display: 'flex' }}>
                          <I.check />
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            </>
          )}

          {/* Actions */}
          <div style={{
            borderTop: hasMultiple ? '1px solid var(--border)' : 'none',
            display: 'flex', flexDirection: 'column',
          }}>
            <button
              type="button"
              data-testid="account-panel-settings"
              onClick={goSettings}
              style={{
                display: 'flex', alignItems: 'center', gap: 10,
                padding: '10px 14px', background: 'transparent', border: 'none',
                textAlign: 'left', cursor: 'pointer', fontFamily: 'inherit',
                fontSize: 12.5, color: 'var(--text-primary)',
              }}
              onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-hover)'}
              onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
            >
              <span style={{ width: 22, display: 'inline-flex', justifyContent: 'center', color: 'var(--text-muted)' }}>
                <I.gear />
              </span>
              <span>Nastavenia</span>
            </button>

            <button
              type="button"
              data-testid="account-panel-signout"
              onClick={() => { close(); signOut(); }}
              style={{
                display: 'flex', alignItems: 'center', gap: 10,
                padding: '10px 14px', background: 'transparent', border: 'none',
                borderTop: '1px solid var(--border)',
                textAlign: 'left', cursor: 'pointer', fontFamily: 'inherit',
                fontSize: 12.5, color: 'var(--text-muted)',
              }}
              onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-hover)'}
              onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
            >
              <span style={{ width: 22, display: 'inline-flex', justifyContent: 'center' }}>
                <I.signOut />
              </span>
              <span>Odhlásiť sa</span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
